import React from 'react';
import { Marker, Tooltip, Popup, Polyline, Circle } from 'react-leaflet';
import L from 'leaflet';
import { dailySubsistenceStatus, getSubsistenceStatusForDay, timePointToDay, subsistenceStatusColors, subsistenceNodes, subsistenceDistributionCorridors } from './subsistenceData';
import { convoyRouteSegments, alternativeRouteSegment } from './convoyData';
// Create custom icon for subsistence status markers
const createSubsistenceStatusIcon = (day: number, percentage: number, status: string, waterDiscipline?: string) => {
  // Map status to color
  const color = status === 'green' ? '#059669' : status === 'amber' ? '#D97706' : status === 'red' ? '#DC2626' : '#1F2937';
  // Calculate progress bar width based on percentage
  const progressWidth = Math.max(10, Math.min(100, percentage)); // Min 10% for visibility, max 100%
  // Water discipline indicator
  const waterIndicator = waterDiscipline === 'active' ? '<div style="background-color: #3B82F6; color: white; font-weight: bold; padding: 1px 4px; border-radius: 2px; font-size: 8px; margin-top: 2px;">WATER DISCIPLINE</div>' : waterDiscipline === 'critical' ? '<div style="background-color: #DC2626; color: white; font-weight: bold; padding: 1px 4px; border-radius: 2px; font-size: 8px; margin-top: 2px;">CRITICAL WATER</div>' : '';
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center; width: 100px;">
        <div style="display: flex; align-items: center; background-color: white; padding: 4px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); width: 100%;">
          <div style="font-size: 16px; margin-right: 4px;">🍽️</div>
          <div style="flex-grow: 1; background-color: #f3f4f6; height: 8px; border-radius: 4px; overflow: hidden;">
            <div style="background-color: ${color}; height: 100%; width: ${progressWidth}%;"></div>
          </div>
          <div style="margin-left: 4px; font-weight: bold; font-size: 12px;">${percentage}%</div>
        </div>
        <div style="background-color: ${color}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          Day ${day}
        </div>
        ${waterIndicator}
      </div>
    `,
    className: '',
    iconSize: [100, 50],
    iconAnchor: [50, 25]
  });
};
// Create custom icon for subsistence nodes
const createSubsistenceNodeIcon = (isAlternate: boolean) => {
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="display: flex; align-items: center; justify-content: center; background-color: white; width: 36px; height: 36px; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2); border: 3px solid ${isAlternate ? '#8B5CF6' : '#047857'};">
          <div style="font-size: 18px;">🍽️</div>
        </div>
        <div style="background-color: ${isAlternate ? '#8B5CF6' : '#047857'}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          ${isAlternate ? 'ALT' : 'F1'}
        </div>
      </div>
    `,
    className: '',
    iconSize: [36, 58],
    iconAnchor: [18, 40]
  });
};
interface SubsistenceLayerProps {
  selectedTimePoint: string;
  useAlternativeRoute?: boolean;
}
export const SubsistenceLayer: React.FC<SubsistenceLayerProps> = ({
  selectedTimePoint,
  useAlternativeRoute = false
}) => {
  // Convert selected time point to day number
  const currentDay = timePointToDay(selectedTimePoint);
  // Get the route segments based on the selected route
  const routeSegments = convoyRouteSegments.filter(segment => {
    // For day 1, check if we should use the alternative route
    if (segment.day === 1 && useAlternativeRoute) {
      return false;
    }
    return true;
  });
  // Add alternative route if selected
  const allSegments = useAlternativeRoute ? [...routeSegments.filter(segment => segment.day !== 1), alternativeRouteSegment] : routeSegments;
  // Sort segments by day
  const sortedSegments = allSegments.sort((a, b) => a.day - b.day);
  // Filter subsistence nodes based on route
  const visibleNodes = subsistenceNodes.filter(node => useAlternativeRoute ? node.isAlternate : !node.isAlternate);
  // Filter distribution corridors based on route
  const visibleCorridors = subsistenceDistributionCorridors.filter(corridor => {
    const node = visibleNodes.find(node => node.id === corridor.nodeId);
    return !!node;
  });
  return <>
      {/* Render subsistence nodes */}
      {visibleNodes.map(node => <Marker key={`subsistence-node-${node.id}`} position={node.coordinates} icon={createSubsistenceNodeIcon(node.isAlternate)} data-id="element-556">
          <Popup data-id="element-557">
            <div className="min-w-[200px]" data-id="element-558">
              <h3 className="font-bold text-base" data-id="element-559">{node.name}</h3>
              <p className="text-sm mt-2" data-id="element-560">{node.description}</p>
              {currentDay >= 5 && <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm" data-id="element-561">
                  <span className="font-medium" data-id="element-562">Resupply Complete</span> -
                  Ration levels restored to 100%
                </div>}
              {currentDay >= 1 && currentDay < 5 && <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm" data-id="element-563">
                  <span className="font-medium" data-id="element-564">Next Resupply:</span> Day 5
                </div>}
            </div>
          </Popup>
        </Marker>)}
      {/* Render subsistence distribution corridors */}
      {visibleCorridors.map(corridor => <Polyline key={`subsistence-corridor-${corridor.id}`} positions={corridor.coordinates} pathOptions={{
      color: '#047857',
      weight: 3,
      opacity: 0.7,
      dashArray: '5, 10'
    }} data-id="element-565">
          <Popup data-id="element-566">
            <div data-id="element-567">
              <h3 className="font-bold" data-id="element-568">{corridor.name}</h3>
              <p className="text-sm mt-1" data-id="element-569">{corridor.description}</p>
              <p className="text-sm mt-2" data-id="element-570">
                <span className="font-medium" data-id="element-571">Status:</span>{' '}
                {currentDay >= 1 ? 'Active' : 'Planned'}
              </p>
            </div>
          </Popup>
        </Polyline>)}
      {/* Render subsistence status markers for each day */}
      {sortedSegments.map(segment => {
      const subsistenceStatus = getSubsistenceStatusForDay(segment.day);
      // Get the coordinates for the marker - use the last point of each segment
      const markerPosition = segment.coordinates[segment.coordinates.length - 1];
      // Only show days up to the current selected day
      if (segment.day > currentDay && currentDay !== 0) {
        return null;
      }
      return <Marker key={`subsistence-status-day-${segment.day}`} position={[markerPosition[0] + 0.05, markerPosition[1] - 0.05]} // Offset slightly from other markers
      icon={createSubsistenceStatusIcon(segment.day, subsistenceStatus.percentageOnHand, subsistenceStatus.status, subsistenceStatus.waterDiscipline)} data-id="element-572">
            <Popup data-id="element-573">
              <div className="min-w-[200px]" data-id="element-574">
                <h3 className="font-bold text-base" data-id="element-575">
                  Class I Subsistence Status - Day {segment.day}
                </h3>
                <div className="mt-2 space-y-2 text-sm" data-id="element-576">
                  <div data-id="element-577">
                    <span className="font-medium" data-id="element-578">Location: </span>
                    {segment.name.split(': ')[1]}
                  </div>
                  <div data-id="element-579">
                    <span className="font-medium" data-id="element-580">Status: </span>
                    <span className={`font-medium ${subsistenceStatus.status === 'green' ? 'text-green-600' : subsistenceStatus.status === 'amber' ? 'text-amber-600' : subsistenceStatus.status === 'red' ? 'text-red-600' : 'text-gray-800'}`} data-id="element-581">
                      {subsistenceStatus.status === 'green' && '🟢 Green'}
                      {subsistenceStatus.status === 'amber' && '🟠 Amber'}
                      {subsistenceStatus.status === 'red' && '🔴 Red'}
                      {subsistenceStatus.status === 'black' && '⚫ Black'}
                    </span>
                  </div>
                  <div data-id="element-582">
                    <span className="font-medium" data-id="element-583">Rations On Hand: </span>
                    {subsistenceStatus.percentageOnHand}%
                  </div>
                  {subsistenceStatus.waterDiscipline && <div data-id="element-584">
                      <span className="font-medium" data-id="element-585">Water Discipline: </span>
                      <span className={`${subsistenceStatus.waterDiscipline === 'normal' ? 'text-green-600' : subsistenceStatus.waterDiscipline === 'active' ? 'text-blue-600' : 'text-red-600'}`} data-id="element-586">
                        {subsistenceStatus.waterDiscipline === 'normal' && 'Normal'}
                        {subsistenceStatus.waterDiscipline === 'active' && 'Active'}
                        {subsistenceStatus.waterDiscipline === 'critical' && 'Critical'}
                      </span>
                    </div>}
                  <div data-id="element-587">
                    <span className="font-medium" data-id="element-588">Note: </span>
                    {subsistenceStatus.notes}
                  </div>
                  {segment.day === 4 && <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm" data-id="element-589">
                      <span className="font-medium" data-id="element-590">Action: </span>
                      Prepare for ration resupply tomorrow
                    </div>}
                  {segment.day === 5 && <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm" data-id="element-591">
                      <span className="font-medium" data-id="element-592">Resupply Complete</span> -
                      Ration levels restored to 100%
                    </div>}
                </div>
              </div>
            </Popup>
          </Marker>;
    })}
      {/* Render a summary table when clicking on the route */}
      {sortedSegments.length > 0 && <Polyline positions={sortedSegments.flatMap(segment => segment.coordinates)} pathOptions={{
      color: 'transparent',
      weight: 20,
      opacity: 0.01
    }} data-id="element-593">
          <Popup data-id="element-594">
            <div className="min-w-[280px]" data-id="element-595">
              <h3 className="font-bold text-base mb-2" data-id="element-596">
                Class I Subsistence Status Summary
              </h3>
              <table className="w-full text-sm border-collapse" data-id="element-597">
                <thead data-id="element-598">
                  <tr className="bg-gray-100" data-id="element-599">
                    <th className="border px-2 py-1 text-left" data-id="element-600">Day</th>
                    <th className="border px-2 py-1 text-left" data-id="element-601">Location</th>
                    <th className="border px-2 py-1 text-left" data-id="element-602">Status</th>
                    <th className="border px-2 py-1 text-right" data-id="element-603">On Hand</th>
                  </tr>
                </thead>
                <tbody data-id="element-604">
                  {dailySubsistenceStatus.map(status => {
                const segment = sortedSegments.find(s => s.day === status.day);
                const location = segment ? segment.name.split(': ')[1] : '—';
                return <tr key={`summary-day-${status.day}`} className={status.day === 5 ? 'bg-green-50' : ''} data-id="element-605">
                        <td className="border px-2 py-1" data-id="element-606">{status.day}</td>
                        <td className="border px-2 py-1" data-id="element-607">{location}</td>
                        <td className="border px-2 py-1" data-id="element-608">
                          <span className={`inline-block w-3 h-3 rounded-full mr-1 ${status.status === 'green' ? 'bg-green-600' : status.status === 'amber' ? 'bg-amber-600' : status.status === 'red' ? 'bg-red-600' : 'bg-gray-800'}`} data-id="element-609"></span>
                          {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
                        </td>
                        <td className="border px-2 py-1 text-right font-medium" data-id="element-610">
                          {status.percentageOnHand}%
                          {status.day === 5 && <span className="ml-1 text-xs text-green-600" data-id="element-611">
                              (Resupply)
                            </span>}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
              <div className="mt-3 text-xs text-gray-500" data-id="element-612">
                <div className="flex items-center" data-id="element-613">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-green-600" data-id="element-614"></span>
                  <span data-id="element-615">Green: ≥75% rations available</span>
                </div>
                <div className="flex items-center" data-id="element-616">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-amber-600" data-id="element-617"></span>
                  <span data-id="element-618">Amber: 50-74% rations available</span>
                </div>
                <div className="flex items-center" data-id="element-619">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-red-600" data-id="element-620"></span>
                  <span data-id="element-621">Red: 25-49% rations available</span>
                </div>
                <div className="flex items-center" data-id="element-622">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-gray-800" data-id="element-623"></span>
                  <span data-id="element-624">Black: &lt;25% rations available</span>
                </div>
              </div>
            </div>
          </Popup>
        </Polyline>}
    </>;
};